import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import FocusLock from 'react-focus-lock';
import Card from 'components/Card';
import { ModalStackContext } from 'components/Modal/ModalManager';
import Portal from 'components/Portal';
import CenterContent from 'components/CenterContent';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import CoachAiAvatar from 'components/Avatar/CoachAIAvatar';
import TooltipButton from 'components/TooltipButton';
import Flex, { Box } from 'components/Flex';
import Text from 'components/Text';
import { ResizeHandleHorizontal, ResizeHandleVertical, HANDLE_PX } from 'components/ResizeHandle';
import { INSET_PX, PANEL_HEADER_HEIGHT, PANEL_HEADER_HEIGHT_PX } from './constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function FloatingDock(_ref) {
  var containerId = _ref.containerId,
    isOpen = _ref.isOpen,
    setIsOpen = _ref.setIsOpen,
    isHiddenByPage = _ref.isHiddenByPage,
    hasInitialized = _ref.hasInitialized,
    loadingStatus = _ref.loadingStatus,
    setLoadingStatus = _ref.setLoadingStatus,
    handleOpen = _ref.handleOpen,
    coachAiName = _ref.coachAiName,
    modals = _ref.modals,
    _ref$geometry = _ref.geometry,
    panelWidth = _ref$geometry.panelWidth,
    panelHeight = _ref$geometry.panelHeight,
    panelPosition = _ref$geometry.panelPosition,
    isFullscreen = _ref$geometry.isFullscreen,
    setIsFullscreen = _ref$geometry.setIsFullscreen,
    startPanelDrag = _ref$geometry.startPanelDrag,
    startFloatingHeightDrag = _ref$geometry.startFloatingHeightDrag,
    startFloatingWidthDrag = _ref$geometry.startFloatingWidthDrag,
    startFloatingBottomDrag = _ref$geometry.startFloatingBottomDrag,
    startFloatingRightDrag = _ref$geometry.startFloatingRightDrag;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var modalStack = useContext(ModalStackContext);
  var shouldHide = isHiddenByPage;
  // Kept in DOM (just CSS-hidden) rather than unmounted while shouldHide, so a
  // live agent thread survives being suppressed by an embedded agent
  // elsewhere on the page — same reasoning as SidebarDock's isPanelVisible.
  var isPanelVisible = isOpen && !shouldHide;
  var agentContainerHeight = isFullscreen ? window.innerHeight - INSET_PX * 2 - PANEL_HEADER_HEIGHT_PX - HANDLE_PX : panelHeight - PANEL_HEADER_HEIGHT_PX - HANDLE_PX * 2;
  return /*#__PURE__*/_jsxs(Portal, {
    children: [!shouldHide && /*#__PURE__*/_jsx(TooltipButton, {
      round: true,
      iconOnly: true,
      variant: "transparent",
      icon: /*#__PURE__*/_jsx(Box, {
        mr: "-xs",
        ml: "-xs",
        children: /*#__PURE__*/_jsx(CoachAiAvatar, {
          scale: 1.5
        })
      }),
      position: "fixed",
      z: "alerts",
      tooltipPlacement: "top-end",
      onClick: function onClick() {
        return isOpen ? setIsOpen(false) : handleOpen();
      },
      style: {
        right: INSET_PX,
        bottom: INSET_PX
      },
      children: t('coach_ai_ask', {
        coachAiName: coachAiName
      })
    }), (isOpen || hasInitialized) && /*#__PURE__*/_jsx(FocusLock, {
      group: "hub-overlay",
      disabled: !isPanelVisible || modalStack.length === 0,
      returnFocus: false,
      children: /*#__PURE__*/_jsxs(Card, {
        position: "fixed",
        z: "alerts",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        variant: "raised",
        style: isFullscreen ? {
          top: INSET_PX,
          left: INSET_PX,
          right: INSET_PX,
          bottom: INSET_PX,
          width: 'auto',
          height: 'auto',
          display: isPanelVisible ? 'flex' : 'none'
        } : {
          width: panelWidth,
          height: panelHeight,
          left: panelPosition.left,
          top: panelPosition.top,
          display: isPanelVisible ? 'flex' : 'none'
        },
        children: [!isFullscreen && /*#__PURE__*/_jsx(ResizeHandleHorizontal, {
          onPointerDown: startFloatingHeightDrag
        }), !isFullscreen && /*#__PURE__*/_jsx(ResizeHandleVertical, {
          left: 0,
          onPointerDown: startFloatingWidthDrag
        }), !isFullscreen && /*#__PURE__*/_jsx(ResizeHandleVertical, {
          right: 0,
          onPointerDown: startFloatingRightDrag
        }), /*#__PURE__*/_jsxs(Flex, {
          gap: "xs",
          px: "s",
          height: PANEL_HEADER_HEIGHT,
          shrink: 0,
          borderBottom: "thin",
          onPointerDown: startPanelDrag,
          style: {
            cursor: 'grab'
          },
          children: [/*#__PURE__*/_jsx(CoachAiAvatar, {}), /*#__PURE__*/_jsx(Box, {
            grow: 1,
            children: /*#__PURE__*/_jsx(Text, {
              fontWeight: "bold",
              children: coachAiName
            })
          }), /*#__PURE__*/_jsx(Button, {
            iconOnly: true,
            icon: isFullscreen ? 'fullscreen-exit' : 'fullscreen',
            variant: "transparent",
            onClick: function onClick() {
              return setIsFullscreen(function (f) {
                return !f;
              });
            },
            children: t(isFullscreen ? 'actions_exit_fullscreen' : 'actions_fullscreen')
          }), /*#__PURE__*/_jsx(Button, {
            iconOnly: true,
            icon: "x",
            variant: "transparent",
            onClick: function onClick() {
              return setIsOpen(false);
            },
            children: t('actions_close')
          })]
        }), /*#__PURE__*/_jsx(Box, {
          id: containerId,
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
          style: {
            height: agentContainerHeight
          },
          onLoadCapture: function onLoadCapture() {
            return setLoadingStatus('ready');
          },
          children: loadingStatus === 'loading' && /*#__PURE__*/_jsx(CenterContent, {
            fillParent: true,
            children: /*#__PURE__*/_jsx(Spinner, {})
          })
        }), !isFullscreen && /*#__PURE__*/_jsx(ResizeHandleHorizontal, {
          onPointerDown: startFloatingBottomDrag
        })]
      })
    }), modals]
  });
}